/**
 * TEMP-H-009: QC Event Card Component
 * 
 * Specialized renderer for QC events showing persona labels, weighted scores,
 * category sub-scores, and links to full QC reports.
 */

import React, { memo } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  User, 
  BarChart3, 
  ExternalLink,
  Hash
} from 'lucide-react';
import type { TimelineEvent } from '@/types/execution-events';
import { COMMON_CATEGORIES } from '@/types/stage-settings';

interface QcEventCardProps {
  event: TimelineEvent;
  compact?: boolean;
}

interface CategoryScore {
  category: string;
  score: number;
  weight?: number;
  threshold?: number;
}

const CategoryScoreBar: React.FC<{ 
  category: CategoryScore; 
  isFailure?: boolean;
}> = ({ category, isFailure }) => {
  const categoryConfig = COMMON_CATEGORIES[category.category];
  const percentage = Math.round(category.score * 100);
  const thresholdPercentage = category.threshold ? Math.round(category.threshold * 100) : null;
  
  // Color logic
  const getScoreColor = () => {
    if (isFailure) return 'bg-red-500';
    if (category.score >= 0.8) return 'bg-green-500';
    if (category.score >= 0.6) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getTextColor = () => {
    if (isFailure) return 'text-red-300';
    if (category.score >= 0.8) return 'text-green-300';
    if (category.score >= 0.6) return 'text-yellow-300';
    return 'text-red-300';
  };

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-1">
          {categoryConfig?.icon && <span>{categoryConfig.icon}</span>}
          <span className="text-gray-300 capitalize">
            {categoryConfig?.name || category.category.replace(/_/g, ' ')}
          </span>
          {category.weight && (
            <span className="text-gray-500">
              (weight: {Math.round(category.weight * 100)}%)
            </span>
          )}
        </div>
        <div className={`font-mono ${getTextColor()}`}>
          {percentage}%
          {thresholdPercentage && (
            <span className="text-gray-500 ml-1">
              / {thresholdPercentage}%
            </span>
          )}
        </div>
      </div>
      
      <div 
        className="relative h-2 bg-gray-700 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={category.score}
        aria-valuemin={0}
        aria-valuemax={1}
        aria-label={`${category.category} quality score: ${(category.score * 100).toFixed(1)}% ${category.threshold ? `(threshold: ${(category.threshold * 100).toFixed(0)}%)` : ''}`}
      >
        {/* Threshold line */}
        {thresholdPercentage && (
          <div 
            className="absolute top-0 w-0.5 h-full bg-gray-400 z-10"
            style={{ left: `${thresholdPercentage}%` }}
            aria-hidden="true"
          />
        )}
        
        {/* Score bar */}
        <div 
          className={`h-full transition-all duration-300 ${getScoreColor()}`}
          style={{ width: `${percentage}%` }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export const QcEventCard = memo(function QcEventCard({
  event,
  compact = false
}: QcEventCardProps) {
  // Extract QC data from event payload
  const qcPayload = event.payload || {};
  const personaSlug = qcPayload.persona_slug || qcPayload.personaSlug;
  const weightedScore = qcPayload.weighted_score || qcPayload.weightedScore;
  const categoryScores = qcPayload.category_scores || qcPayload.categoryScores || {};
  const categoryWeights = qcPayload.category_weights || qcPayload.categoryWeights;
  const hardFailCategories = qcPayload.hard_fail_categories || qcPayload.hardFailCategories || [];
  const passed = qcPayload.passed;
  const borderline = qcPayload.borderline;
  const targetStage = qcPayload.target_stage || qcPayload.targetStage;
  const rubricHash = qcPayload.rubricHash || qcPayload.rubric_hash;

  // Convert category scores to array with weights
  const categoryList: CategoryScore[] = Object.entries(categoryScores).map(([category, score]) => ({
    category,
    score: typeof score === 'number' ? score : 0,
    weight: categoryWeights?.[category],
    threshold: 0.7 // Default threshold, could come from persona config
  }));

  // Overall status
  const getOverallStatus = () => {
    if (hardFailCategories.length > 0) return 'failed';
    if (passed === false) return 'failed';
    if (borderline) return 'borderline';
    if (passed === true) return 'passed';
    return 'unknown';
  };

  const status = getOverallStatus();
  const overallPercentage = Math.round((weightedScore || 0) * 100);

  const getStatusIcon = () => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-4 h-4 text-green-400" aria-label="Quality check passed" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" aria-label="Quality check failed" />;
      case 'borderline':
        return <AlertTriangle className="w-4 h-4 text-yellow-400" aria-label="Quality check borderline" />;
      default:
        return <BarChart3 className="w-4 h-4 text-blue-400" aria-label="Quality check status unknown" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'passed': return 'border-l-green-500';
      case 'failed': return 'border-l-red-500';
      case 'borderline': return 'border-l-yellow-500';
      default: return 'border-l-blue-500';
    }
  };

  return (
    <div 
      className={`border-l-2 ${getStatusColor()} bg-gray-900/50`}
      role="article"
      aria-labelledby={`qc-card-${event.execution_id}-${event.stage || targetStage}`}
      aria-describedby={`qc-summary-${event.execution_id}-${event.stage || targetStage}`}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start gap-3 mb-3">
          <div className="mt-1">
            {getStatusIcon()}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex-1 min-w-0">
                <h4 
                  className="text-sm font-medium text-white"
                  id={`qc-card-${event.execution_id}-${event.stage || targetStage}`}
                >
                  {event.displayTitle}
                </h4>
                {event.displayDescription && (
                  <p 
                    className="text-xs text-gray-400 mt-0.5"
                    id={`qc-summary-${event.execution_id}-${event.stage || targetStage}`}
                  >
                    {event.displayDescription}
                  </p>
                )}
              </div>
              
              {/* Overall Score */}
              <div className="flex items-center gap-3">
                {typeof weightedScore === 'number' && (
                  <div 
                    className="text-right"
                    role="status" 
                    aria-label={`Overall quality score: ${overallPercentage}%, ${status}`}
                  >
                    <div className={`text-lg font-mono ${
                      status === 'passed' ? 'text-green-400' :
                      status === 'failed' ? 'text-red-400' :
                      status === 'borderline' ? 'text-yellow-400' : 'text-blue-400'
                    }`}>
                      {overallPercentage}%
                    </div>
                    <div className="text-xs text-gray-500">Overall</div>
                  </div>
                )}
                
                {/* Status Badge */}
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  status === 'passed' ? 'bg-green-500/20 text-green-300' :
                  status === 'failed' ? 'bg-red-500/20 text-red-300' :
                  status === 'borderline' ? 'bg-yellow-500/20 text-yellow-300' : 
                  'bg-blue-500/20 text-blue-300'
                }`}>
                  {status === 'borderline' ? 'Borderline' : 
                   status === 'failed' ? 'Failed' :
                   status === 'passed' ? 'Passed' : 'QC Complete'}
                </div>
              </div>
            </div>

            {/* Persona and Target Stage */}
            <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
              {personaSlug && (
                <div className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  <span className="font-medium text-purple-300">{personaSlug}</span>
                </div>
              )}
              
              {targetStage && (
                <div className="flex items-center gap-1">
                  <span>Target: </span>
                  <span className="font-medium capitalize">{targetStage}</span>
                </div>
              )}
              
              {rubricHash && (
                <div className="flex items-center gap-1">
                  <Hash className="w-3 h-3" />
                  <span className="font-mono">{rubricHash.slice(0, 8)}</span>
                </div>
              )}
            </div>

            {/* Hard Fail Categories */}
            {hardFailCategories.length > 0 && (
              <div className="mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded">
                <div className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="text-xs font-medium text-red-300 mb-1">
                      Hard Failures ({hardFailCategories.length})
                    </div>
                    <div className="text-xs text-red-400">
                      {hardFailCategories.map((category: string) => 
                        COMMON_CATEGORIES[category]?.name || category.replace(/_/g, ' ')
                      ).join(', ')}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Category Scores */}
            {!compact && categoryList.length > 0 && (
              <div className="space-y-3">
                <h5 className="text-xs font-medium text-gray-400 flex items-center gap-2">
                  <BarChart3 className="w-3 h-3" />
                  Category Scores ({categoryList.length})
                </h5>
                
                <div className="grid grid-cols-1 gap-3">
                  {categoryList.map((category) => (
                    <CategoryScoreBar
                      key={category.category}
                      category={category}
                      isFailure={hardFailCategories.includes(category.category)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Compact Category Summary */}
            {compact && categoryList.length > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500">Categories:</span>
                <div className="flex items-center gap-1">
                  {categoryList.slice(0, 3).map(category => {
                    const percentage = Math.round(category.score * 100);
                    const isLow = category.score < 0.7;
                    return (
                      <span key={category.category} className={`px-1.5 py-0.5 rounded text-xs ${
                        isLow ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'
                      }`}>
                        {percentage}%
                      </span>
                    );
                  })}
                  {categoryList.length > 3 && (
                    <span className="text-gray-500">+{categoryList.length - 3} more</span>
                  )}
                </div>
              </div>
            )}

            {/* Action Link */}
            {event.correlation_id && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <button className="flex items-center gap-2 text-xs text-purple-400 hover:text-purple-300 transition-colors">
                  <ExternalLink className="w-3 h-3" />
                  View Full QC Report
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default QcEventCard;